/**
 * DragDropEditor.jsx
 * Admin content editor for lesson_type === 'drag_drop'
 *
 * Content shape:
 *   { sentences: [{ scrambled, correct_order, hint }] }
 *
 * The admin enters correct_order as a space-separated sentence.
 * scrambled is automatically a shuffled copy of correct_order (saved on blur).
 *
 * Props:
 *   value    — current content object
 *   onChange — (newContent) => void
 */


import { Plus, Trash2, ChevronUp, ChevronDown, Shuffle } from 'lucide-react';
import { Input } from '../../ui/input';
import { Button } from '../../ui/button';
import { useLanguage } from '../../../contexts/LanguageContext';

const DEFAULT_SENTENCE = { scrambled: [], correct_order: [], hint: '' };

function shuffleArray(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

export default function DragDropEditor({ value, onChange }) {
  const { t } = useLanguage();
  const sentences = Array.isArray(value?.sentences) ? value.sentences : [];

  const update = (next) => onChange({ ...value, sentences: next });

  const addSentence = () => update([...sentences, { ...DEFAULT_SENTENCE }]);

  const removeSentence = (idx) => update(sentences.filter((_, i) => i !== idx));

  const updateSentence = (idx, field, val) =>
    update(sentences.map((s, i) => (i === idx ? { ...s, [field]: val } : s)));

  const handleCorrectOrderChange = (idx, raw) => {
    const words = raw.split(/\s+/).filter(Boolean);
    const scrambled = shuffleArray(words);
    update(
      sentences.map((s, i) =>
        i === idx ? { ...s, correct_order: words, scrambled } : s,
      ),
    );
  };

  const rescramble = (idx) => {
    const s = sentences[idx];
    const scrambled = shuffleArray([...s.correct_order]);
    update(sentences.map((item, i) => (i === idx ? { ...item, scrambled } : item)));
  };

  const moveUp = (idx) => {
    if (idx === 0) return;
    const next = [...sentences];
    [next[idx - 1], next[idx]] = [next[idx], next[idx - 1]];
    update(next);
  };

  const moveDown = (idx) => {
    if (idx === sentences.length - 1) return;
    const next = [...sentences];
    [next[idx], next[idx + 1]] = [next[idx + 1], next[idx]];
    update(next);
  };

  return (
    <div className="space-y-3">
      {sentences.length === 0 && (
        <p className="text-xs text-center text-[var(--text-muted)] py-4 border-2 border-dashed border-[var(--border-light)] rounded-xl">
          {t('lessonEditor.common.emptyList')}
        </p>
      )}
      {sentences.map((s, idx) => (
        <div
          key={idx}
          className="bg-[var(--bg-app)] border-2 border-[var(--border-light)] rounded-xl p-4 space-y-2"
        >
          <div className="flex items-center justify-between mb-1">
            <span className="text-xs font-bold text-[var(--text-muted)]">#{idx + 1}</span>
            <div className="flex gap-1">
              <button
                type="button"
                onClick={() => moveUp(idx)}
                disabled={idx === 0}
                aria-label={t('lessonEditor.common.moveUp')}
                className="p-1 rounded hover:bg-white disabled:opacity-30 text-[var(--text-muted)] hover:text-[var(--brand-primary)]"
              >
                <ChevronUp size={14} />
              </button>
              <button
                type="button"
                onClick={() => moveDown(idx)}
                disabled={idx === sentences.length - 1}
                aria-label={t('lessonEditor.common.moveDown')}
                className="p-1 rounded hover:bg-white disabled:opacity-30 text-[var(--text-muted)] hover:text-[var(--brand-primary)]"
              >
                <ChevronDown size={14} />
              </button>
              <button
                type="button"
                onClick={() => removeSentence(idx)}
                aria-label={t('lessonEditor.dragDrop.removeSentence')}
                className="p-1 rounded hover:bg-red-50 text-[var(--text-muted)] hover:text-[var(--brand-danger)]"
              >
                <Trash2 size={14} />
              </button>
            </div>
          </div>

          {/* Correct order input */}
          <div>
            <label className="text-xs font-bold text-[var(--text-muted)] mb-1 block">
              {t('lessonEditor.dragDrop.sentence')} * ({t('lessonEditor.dragDrop.sentenceHint')})
            </label>
            <div className="flex gap-2">
              <Input
                value={s.correct_order.join(' ')}
                onChange={(e) => handleCorrectOrderChange(idx, e.target.value)}
                placeholder={t('lessonEditor.dragDrop.sentencePlaceholder')}
                className="rounded-xl border-2 border-[var(--border-light)] text-sm flex-1"
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => rescramble(idx)}
                title={t('lessonEditor.dragDrop.rescramble')}
                className="rounded-xl border-2 border-[var(--border-light)] px-2"
              >
                <Shuffle size={14} />
              </Button>
            </div>
          </div>

          {/* Scrambled preview */}
          {s.scrambled && s.scrambled.length > 0 && (
            <div>
              <label className="text-xs font-bold text-[var(--text-muted)] mb-1 block">
                {t('lessonEditor.dragDrop.scrambledPreview')}
              </label>
              <div className="flex flex-wrap gap-1.5">
                {s.scrambled.map((word, wi) => (
                  <span
                    key={wi}
                    className="px-2 py-0.5 bg-amber-100 text-amber-800 rounded-lg text-xs font-bold border border-amber-200"
                  >
                    {word}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Hint */}
          <div>
            <label className="text-xs font-bold text-[var(--text-muted)] mb-1 block">
              {t('lessonEditor.dragDrop.hint')}
            </label>
            <Input
              value={s.hint}
              onChange={(e) => updateSentence(idx, 'hint', e.target.value)}
              placeholder={t('lessonEditor.dragDrop.hintPlaceholder')}
              className="rounded-xl border-2 border-[var(--border-light)] text-sm"
            />
          </div>
        </div>
      ))}
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={addSentence}
        className="rounded-xl border-2 border-dashed border-[var(--border-heavy)] text-[var(--text-muted)] hover:border-[var(--brand-primary)] hover:text-[var(--brand-primary)] font-bold w-full"
      >
        <Plus size={14} className="mr-1" /> {t('lessonEditor.dragDrop.addSentence')}
      </Button>
    </div>
  );
}
